import type { LightboxItem } from './lightbox';

/** Generic file shape most surfaces already have — mapped into a LightboxItem. */
export interface LightboxFile {
  id: string;
  name: string;
  contentType?: string | null;
  /** Full file URL — drives the preview and download. */
  url?: string | null;
  /** Image thumbnail (strip + grid). */
  thumbnailUrl?: string | null;
  /** Full-size stage image when it differs from `url` (e.g. an imagekit full url). */
  imageUrl?: string | null;
}

// Renderable-in-`<img>` image extensions (so HEIC/HEIF stay out — they wouldn't
// render in a raw stage). Trailing query/hash allowed for presigned urls.
const IMAGE_EXTENSION = /\.(png|jpe?g|gif|webp|svg|avif|bmp|ico)(\?|#|$)/i;
const hasImageExtension = (value?: string | null) => (value ? IMAGE_EXTENSION.test(value) : false);

// Trailing query/hash allowed so a presigned PDF url (`doc.pdf?X-Amz-Signature=…`)
// is still detected — a bare `.endsWith('.pdf')` would miss it.
const PDF_EXTENSION = /\.pdf(\?|#|$)/i;

/** Shared PDF check — exported so surfaces classify files against one source
 *  instead of re-deriving it (must stay in sync with `isImageFile`). */
export const isPdf = (file: LightboxFile) =>
  file.contentType === 'application/pdf' || PDF_EXTENSION.test(file.name) || PDF_EXTENSION.test(file.url ?? '');

/** Shared image check — used by both the lightbox stage and the FileList
 *  thumbnail, so a file never previews in one and shows a generic icon in the
 *  other. Classification precedence: content-type, then a recognizable
 *  extension, are authoritative — a non-image (e.g. a PDF) that happens to carry
 *  a `thumbnailUrl` must NOT be treated as an image. Only when neither is
 *  conclusive does a thumbnail/image url stand in (some sources, e.g. product
 *  assets, carry one but no content-type/extension). */
export const isImageFile = (file: LightboxFile): boolean => {
  if (file.contentType) return file.contentType.startsWith('image/');
  if (hasImageExtension(file.name) || hasImageExtension(file.url)) return true;
  if (isPdf(file)) return false;
  return Boolean(file.thumbnailUrl ?? file.imageUrl);
};

export const fileToLightboxItem = (file: LightboxFile): LightboxItem => ({
  key: file.id,
  title: file.name,
  imageUrl: isImageFile(file) ? (file.imageUrl ?? file.url ?? file.thumbnailUrl) : null,
  thumbnailUrl: file.thumbnailUrl,
  pdfUrl: !isImageFile(file) && isPdf(file) ? file.url : null,
  url: file.url ?? '',
  downloadName: file.name,
});
